import { useEffect, useRef, useState, type ReactNode } from 'react';
import { embedUrl, type VideoProvider } from '@wr/shared';
import { drawPage, openPdf, type PdfDocument } from './pdf.js';

/**
 * Full screen, over everything (prototype: .viewer): the video player and
 * the PDF pages, in the library and on the prospect page. Escape closes.
 */
export function Viewer({
  title,
  className,
  tools,
  closeLabel = 'Close',
  onClose,
  children,
}: {
  title: string;
  className?: string;
  tools?: ReactNode;
  closeLabel?: string;
  onClose(): void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={`viewer${className ? ` ${className}` : ''}`} role="dialog" aria-label={title}>
      <header>
        <button type="button" aria-label={closeLabel} onClick={onClose}>
          ×
        </button>
        <span className="ttl">{title}</span>
        {tools}
      </header>
      {children}
    </div>
  );
}

/** The provider's own player (YouTube without cookies, Vimeo without tracking). */
export function VideoOverlay({
  title,
  video,
  closeLabel,
  onClose,
}: {
  title: string;
  video: { provider: VideoProvider; id: string; hash?: string | null };
  closeLabel?: string;
  onClose(): void;
}) {
  return (
    <Viewer title={title} className="player" closeLabel={closeLabel} onClose={onClose}>
      <div className="pstage">
        <iframe
          className="pframe"
          title={title}
          src={embedUrl(video)}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    </Viewer>
  );
}

/** Every page of a PDF, with zoom; Download instead when it cannot be drawn. */
export function PdfOverlay({
  title,
  url,
  texts = {
    opening: 'Opening',
    failed: 'This PDF cannot be shown here.',
    download: 'Download instead',
  },
  closeLabel,
  onClose,
  onDownload,
  load,
}: {
  title: string;
  url: string;
  /** The PDF's bytes from elsewhere (the booth kiosk's copy on the device). */
  load?: () => Promise<ArrayBuffer>;
  texts?: { opening: string; failed: string; download: string };
  closeLabel?: string;
  onClose(): void;
  onDownload(): void;
}) {
  const [doc, setDoc] = useState<PdfDocument | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const pages = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let current = true;
    let opened: PdfDocument | null = null;
    void (async () => {
      try {
        let data: ArrayBuffer;
        if (load) data = await load();
        else {
          const res = await fetch(url, { credentials: 'same-origin' });
          if (!res.ok) throw new Error(String(res.status));
          data = await res.arrayBuffer();
        }
        opened = await openPdf(data);
        if (current) setDoc(opened);
      } catch {
        if (current) setFailed(true);
      }
    })();
    return () => {
      current = false;
      void opened?.loadingTask.destroy();
    };
  }, [url, load]);

  useEffect(() => {
    const box = pages.current;
    if (!doc || !box) return;
    let current = true;
    box.replaceChildren();
    const width = Math.min(box.clientWidth - 24, 1100) * zoom;
    void (async () => {
      for (let n = 1; n <= doc.numPages && current; n++) {
        const canvas = document.createElement('canvas');
        const label = document.createElement('div');
        label.className = 'pg';
        label.textContent = `${n} / ${doc.numPages}`;
        box.append(canvas, label);
        await drawPage(doc, n, width, canvas);
      }
    })();
    return () => {
      current = false;
    };
  }, [doc, zoom]);

  return (
    <Viewer
      title={title}
      closeLabel={closeLabel}
      onClose={onClose}
      tools={
        <>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
          >
            −
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
          >
            +
          </button>
        </>
      }
    >
      <div className="pages" ref={pages}>
        {!doc && (
          <div className="vmsg">
            {!failed && <div className="spinner" />}
            <span>{failed ? texts.failed : texts.opening}</span>
            {failed && (
              <button type="button" className="btn primary" onClick={onDownload}>
                {texts.download}
              </button>
            )}
          </div>
        )}
      </div>
    </Viewer>
  );
}
